/**
 * kits-theme-init.js
 *
 * Purpose: Non-critical page-level enhancements extracted from inline script
 * in theme.liquid. Loaded with `defer` so it never blocks rendering.
 *
 * Contents:
 *   1. Smooth scroll handler for [data-scroll-target]
 *   2. Login/recover form toggle
 *   3. FAQ TOC patching (IntersectionObserver)
 *   4. Mobile chat launcher guard (repositioning)
 *   5. Privacy banner → chat guard
 *   6. Legacy Trustpilot widget cleanup
 *   7. Appointo checkout fast-path (fetch interception)
 *   8. Appointo modal → chat guard
 *
 * @file assets/kits-theme-init.js
 */

(function() {
  'use strict';

  /* ================================================================
     1. SMOOTH SCROLL — [data-scroll-target] buttons
     ================================================================ */
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-scroll-target]');
    if (!btn) return;

    var targetId = btn.getAttribute('data-scroll-target');
    var target = document.getElementById(targetId);
    if (!target) return;

    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });

    var tocContainer = btn.closest('faq-toc');
    if (tocContainer) {
      tocContainer.querySelectorAll('[data-scroll-target]').forEach(function(item) {
        item.classList.toggle('is-active', item === btn);
      });
    }
  });

  /* ================================================================
     2. LOGIN / RECOVER FORM TOGGLE
     ================================================================ */
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-login-toggle]');
    if (!btn) return;

    var targetPanel = btn.getAttribute('data-login-toggle');
    var container = btn.closest('account-login');
    if (!container) return;

    e.preventDefault();

    var loginForm = container.querySelector('#login');
    var recoverForm = container.querySelector('#recover');
    if (!loginForm || !recoverForm) return;

    var fromForm = targetPanel === 'recover' ? loginForm : recoverForm;
    var toForm = targetPanel === 'recover' ? recoverForm : loginForm;

    fromForm.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    fromForm.style.opacity = '0';
    fromForm.style.transform = 'translateY(30px)';

    setTimeout(function() {
      fromForm.hidden = true;
      fromForm.style.removeProperty('opacity');
      fromForm.style.removeProperty('transform');
      fromForm.style.removeProperty('transition');

      toForm.hidden = false;
      toForm.style.opacity = '0';
      toForm.style.transform = 'translateY(30px)';
      toForm.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

      void toForm.offsetWidth;

      toForm.style.opacity = '1';
      toForm.style.transform = 'translateY(0)';

      setTimeout(function() {
        toForm.style.removeProperty('opacity');
        toForm.style.removeProperty('transform');
        toForm.style.removeProperty('transition');
      }, 300);
    }, 300);
  });

  /* ================================================================
     3. FAQ TOC PATCHING — IntersectionObserver for scroll tracking
     ================================================================ */
  function patchFaqToc() {
    var faqTocs = document.querySelectorAll('faq-toc');
    faqTocs.forEach(function(toc) {
      var buttons = Array.from(toc.querySelectorAll('[data-scroll-target]'));
      if (buttons.length === 0) return;

      var targets = buttons.map(function(btn) {
        return document.getElementById(btn.getAttribute('data-scroll-target'));
      }).filter(Boolean);

      if (targets.length === 0) return;

      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          var btn = buttons.find(function(b) {
            return b.getAttribute('data-scroll-target') === entry.target.id;
          });
          if (!btn) return;

          if (!entry.isIntersecting && btn.classList.contains('is-active')) {
            return;
          }
          if (entry.isIntersecting) {
            buttons.forEach(function(b) {
              b.classList.toggle('is-active', b === btn);
            });
          }
        });
      }, { rootMargin: '0px 0px -70% 0px' });

      targets.forEach(function(target) {
        observer.observe(target);
      });
    });
  }

  /* ================================================================
     4. MOBILE CHAT LAUNCHER GUARD
     Delays and repositions chat launcher to avoid overlap with
     cookie/privacy banners on mobile.
     ================================================================ */
  function initMobileChatLauncherGuard() {
    if (window.matchMedia('(min-width: 750px)').matches) return;

    var chatVisible = false;
    var isUpdating = false;
    var mutationTimeout = null;

    function getPrivacyBannerOffset() {
      var candidates = document.querySelectorAll('privacy-banner[open], .privacy-banner[open], .privacy-banner, [role="alertdialog"], [class*="cookie"], [id*="cookie"]');
      var offset = 0;

      candidates.forEach(function(el) {
        if (!(el instanceof HTMLElement)) return;
        var style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return;

        var rect = el.getBoundingClientRect();
        if (rect.height < 60) return;
        if (rect.bottom < window.innerHeight - 8) return;

        offset = Math.max(offset, rect.height + 16);
      });

      return offset;
    }

    function isChatLauncherElement(el) {
      if (!(el instanceof HTMLElement)) return false;
      if (el.matches('div#shopify-chat, #ShopifyChat, inbox-online-store-chat#ShopifyChat, shopify-chat')) return true;
      if (el.id === 'shopify-chat' || el.id === 'ShopifyChat') return true;
      return false;
    }

    function isBottomRightFloatingElement(el) {
      if (!(el instanceof HTMLElement)) return false;
      var style = window.getComputedStyle(el);
      if (style.position !== 'fixed' && style.position !== 'sticky') return false;

      var rect = el.getBoundingClientRect();
      if (rect.width < 32 || rect.height < 32) return false;

      return rect.left > window.innerWidth * 0.45 && rect.top > window.innerHeight * 0.35;
    }

    function getLaunchers() {
      var selectors = ['div#shopify-chat', '#ShopifyChat', 'inbox-online-store-chat#ShopifyChat', 'shopify-chat'];
      var nodes = [];
      selectors.forEach(function(selector) {
        document.querySelectorAll(selector).forEach(function(node) { nodes.push(node); });
      });

      return Array.from(new Set(nodes)).filter(function(node) {
        if (node.closest && node.closest('.mobile-sticky-bar, .product-sticky-bar, .privacy-banner, [role="alertdialog"]')) return false;
        return isChatLauncherElement(node) && isBottomRightFloatingElement(node);
      });
    }

    function applyLauncherState() {
      if (isUpdating) return;
      isUpdating = true;

      try {
        var launchers = getLaunchers();
        var privacyOffset = getPrivacyBannerOffset();

        launchers.forEach(function(launcher) {
          if (!launcher.dataset.ksOriginalBottom) {
            var computedBottom = parseFloat(window.getComputedStyle(launcher).bottom);
            launcher.dataset.ksOriginalBottom = Number.isNaN(computedBottom) ? '16' : String(computedBottom);
          }
          if (!launcher.dataset.ksOriginalRight) {
            var computedRight = parseFloat(window.getComputedStyle(launcher).right);
            launcher.dataset.ksOriginalRight = Number.isNaN(computedRight) ? '16' : String(computedRight);
          }

          var originalBottom = parseFloat(launcher.dataset.ksOriginalBottom || '16');
          var originalRight = parseFloat(launcher.dataset.ksOriginalRight || '16');
          var targetBottom = privacyOffset > 0 ? Math.max(originalBottom, privacyOffset) : originalBottom;

          launcher.style.left = 'auto';
          launcher.style.top = 'auto';
          launcher.style.right = originalRight + 'px';
          launcher.style.bottom = targetBottom + 'px';
          launcher.style.zIndex = '30';
          launcher.style.transition = 'opacity 0.2s ease, bottom 0.2s ease';

          if (!chatVisible) {
            launcher.style.opacity = '0';
            launcher.style.visibility = 'hidden';
            launcher.style.pointerEvents = 'none';
          } else {
            launcher.style.opacity = '';
            launcher.style.visibility = '';
            launcher.style.pointerEvents = '';
          }
        });
      } finally {
        setTimeout(function() { isUpdating = false; }, 50);
      }
    }

    applyLauncherState();

    var mutationObserver = new MutationObserver(function() {
      if (mutationTimeout) return;
      mutationTimeout = setTimeout(function() {
        mutationTimeout = null;
        applyLauncherState();
      }, 100);
    });
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

    var scrollTimeout = null;
    window.addEventListener('scroll', function() {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(function() {
        scrollTimeout = null;
        applyLauncherState();
      }, 100);
    }, { passive: true });

    window.addEventListener('resize', applyLauncherState, { passive: true });

    setTimeout(function() {
      chatVisible = true;
      applyLauncherState();
    }, 10000);
  }

  /* ================================================================
     5. PRIVACY BANNER → CHAT GUARD
     Hides Shopify Inbox when privacy/cookie consent is visible.
     ================================================================ */
  function initPrivacyBannerChatGuard() {
    var style = document.createElement('style');
    style.textContent =
      'body.privacy-banner-open #shopify-chat,' +
      'body.privacy-banner-open div#shopify-chat,' +
      'body.privacy-banner-open #ShopifyChat,' +
      'body.privacy-banner-open inbox-online-store-chat#ShopifyChat,' +
      'body.privacy-banner-open shopify-chat,' +
      'body.privacy-banner-open [id*="ShopifyChat"],' +
      'body.privacy-banner-open [id*="shopify-chat"]{' +
      'opacity:0!important;visibility:hidden!important;pointer-events:none!important}';
    document.head.appendChild(style);

    var bannerSelectors = [
      'section#shopify_pc_banner', 'section#shopify-pc-banner',
      '#shopify_pc_banner', '#shopify-pc-banner',
      'dialog[open]', '[role="dialog"]', '[role="alertdialog"]',
      '[aria-modal="true"]', 'privacy-banner', '.privacy-banner',
      '[class*="cookie"]', '[id*="cookie"]',
      '[class*="consent"]', '[id*="consent"]',
      '[class*="privacy"]', '[id*="privacy"]'
    ].join(', ');

    var isUpdating = false;
    var mutationTimeout = null;

    function isPrivacyLikeBanner(element) {
      if (!(element instanceof HTMLElement)) return false;
      if (element.matches('#shopify_pc_banner, #shopify-pc-banner, section#shopify_pc_banner, section#shopify-pc-banner, privacy-banner, .privacy-banner')) return true;

      var content = [
        element.id || '', element.className || '',
        element.getAttribute('aria-label') || '',
        element.getAttribute('aria-labelledby') || '',
        element.textContent || ''
      ].join(' ').toLowerCase();

      return content.indexOf('we value your privacy') !== -1
        || content.indexOf('manage preferences') !== -1
        || content.indexOf('privacy policy') !== -1
        || content.indexOf('cookie') !== -1
        || content.indexOf('consent') !== -1;
    }

    function isVisibleBanner(element) {
      if (!(element instanceof HTMLElement)) return false;
      if (!isPrivacyLikeBanner(element)) return false;

      var computedStyle = window.getComputedStyle(element);
      if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') return false;
      if (element.hasAttribute('hidden')) return false;
      if (element.tagName === 'DIALOG' && !element.hasAttribute('open')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;

      var rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0) return false;

      return rect.top < window.innerHeight && rect.left < window.innerWidth;
    }

    function updatePrivacyBannerState() {
      if (isUpdating) return;
      isUpdating = true;

      try {
        var banners = document.querySelectorAll(bannerSelectors);
        var isBannerOpen = Array.from(banners).some(isVisibleBanner);
        document.body.classList.toggle('privacy-banner-open', isBannerOpen);
      } finally {
        setTimeout(function() { isUpdating = false; }, 50);
      }
    }

    updatePrivacyBannerState();

    var observer = new MutationObserver(function() {
      if (mutationTimeout) return;
      mutationTimeout = setTimeout(function() {
        mutationTimeout = null;
        updatePrivacyBannerState();
      }, 100);
    });
    observer.observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden']
    });

    document.addEventListener('click', function(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(bannerSelectors)) {
        requestAnimationFrame(function() {
          setTimeout(updatePrivacyBannerState, 50);
        });
      }
    }, true);

    window.addEventListener('resize', updatePrivacyBannerState, { passive: true });
    var scrollTimeout = null;
    window.addEventListener('scroll', function() {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(function() {
        scrollTimeout = null;
        updatePrivacyBannerState();
      }, 200);
    }, { passive: true });
  }

  /* ================================================================
     6. LEGACY TRUSTPILOT WIDGET CLEANUP
     ================================================================ */
  function initTrustpilotWidgetCleanup() {
    var cleanupTimeout = null;

    function removeLegacyTrustpilotWidgets() {
      var selectors = [
        'iframe[title*="Trustpilot"]', 'iframe[src*="widget.trustpilot.com"]',
        '.trustpilot-widget', '[data-trustpilot-widget]', '[class*="trustpilot-widget"]'
      ];
      document.querySelectorAll(selectors.join(', ')).forEach(function(node) {
        var container = node.closest('.shopify-block, .trustpilot-widget, [class*="trustpilot"], [id*="trustpilot"]');
        if (container && !container.matches('#shopify-section-trustpilot_bar, .shopify-section--trustpilot-bar')) {
          container.remove();
        } else {
          node.remove();
        }
      });
    }

    removeLegacyTrustpilotWidgets();

    var observer = new MutationObserver(function() {
      if (cleanupTimeout) return;
      cleanupTimeout = setTimeout(function() {
        cleanupTimeout = null;
        removeLegacyTrustpilotWidgets();
      }, 200);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ================================================================
     7. APPOINTO CHECKOUT FAST-PATH
     Intercepts fetch /cart/add.js during booking flow, opens cart
     drawer instead of navigating to /cart.
     ================================================================ */
  function initAppointoCheckoutFastPath() {
    var armed = false;
    var hasHandled = false;

    function hasAppointmentProperties(item) {
      if (!item || !item.properties) return false;
      return Object.keys(item.properties).some(function(key) {
        var k = String(key).toLowerCase();
        return k === '_start_time' || k === '_timezone'
          || k.indexOf('appointo') !== -1
          || (k.indexOf('date') !== -1 && k.indexOf('time') !== -1);
      });
    }

    function openCartDrawer() {
      if (hasHandled) return;
      hasHandled = true;
      armed = false;

      var appointoModal = document.getElementById('appointo-modal');
      if (appointoModal) appointoModal.style.display = 'none';
      var appointoQuestions = document.getElementById('appointo-custom-questions');
      if (appointoQuestions) appointoQuestions.style.display = 'none';
      document.body.classList.remove('appointo-modal-open');

      var cartDrawer = document.getElementById('cart-drawer');
      if (!cartDrawer) { window.location.href = '/cart'; return; }
      var sectionWrapper = cartDrawer.closest('[id^="shopify-section-"]');
      var sectionId = sectionWrapper ? sectionWrapper.id.replace('shopify-section-', '') : null;
      if (!sectionId) { window.location.href = '/cart'; return; }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js?sections=' + sectionId, { credentials: 'same-origin' })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(cart) {
          if (!cart) { window.location.href = '/cart'; return; }
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cart }
          }));
        })
        .catch(function() { window.location.href = '/cart'; });
    }

    // Fetch interception — returns never-resolving promise for appointment items
    (function interceptFetch() {
      if (typeof window.fetch !== 'function') return;
      var originalFetch = window.fetch;

      window.fetch = function(input, init) {
        var result = originalFetch.apply(this, arguments);
        var url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
        if (!armed || url.indexOf('/cart/add.js') === -1) return result;

        return new Promise(function(resolve, reject) {
          result.then(function(response) {
            response.clone().json().then(function(data) {
              var items = data && data.items ? data.items : (data ? [data] : []);
              if (items.some(hasAppointmentProperties)) {
                openCartDrawer();
                // Never resolve — blocks Appointo's location.href = '/cart'
              } else {
                resolve(response);
              }
            }).catch(function() { resolve(response); });
          }).catch(function(err) { reject(err); });
        });
      };
    })();

    // ARM on appointo-confirm click
    document.addEventListener('click', function(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('#appointo-confirm, [id^="appointo-confirm"], .appointo-confirm')) {
        armed = true;
        hasHandled = false;
      }
    }, true);

    // Fallback: if we land on /cart with appointment items, try opening drawer
    var path = (window.location.pathname || '').replace(/\/+$/, '');
    if (/(^|\/)cart$/.test(path)) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(cart) {
          if (!cart || !Array.isArray(cart.items)) return;
          if (!cart.items.some(hasAppointmentProperties)) return;
          if (!/\/products\//.test(document.referrer || '')) return;
          var cartDrawer = document.getElementById('cart-drawer');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            setTimeout(function() { cartDrawer.show(); }, 300);
          }
        })
        .catch(function() {});
    }
  }

  /* ================================================================
     8. APPOINTO MODAL → CHAT GUARD
     Hides Shopify Inbox when Appointo booking modal is open.
     ================================================================ */
  function initAppointoModalChatGuard() {
    var style = document.createElement('style');
    style.textContent =
      'body.appointo-modal-open #shopify-chat,' +
      'body.appointo-modal-open shopify-chat,' +
      'body.appointo-modal-open iframe[id*="chat"],' +
      'body.appointo-modal-open [id*="ShopifyChat"],' +
      'body.appointo-modal-open [id*="shopify-chat"],' +
      'body.appointo-modal-open .shopify-chat{' +
      'visibility:hidden!important;opacity:0!important;pointer-events:none!important;z-index:-1!important}' +
      'body.appointo-modal-open #appointo-modal{z-index:2147483646!important}' +
      'body.appointo-modal-open #appointo-modal .appointo-modal-content{z-index:2147483646!important}';
    document.head.appendChild(style);

    function checkModalState() {
      var modal = document.getElementById('appointo-modal');
      if (!modal) return;
      var isOpen = modal.style.display !== 'none' && modal.style.display !== '';
      document.body.classList.toggle('appointo-modal-open', isOpen);
    }

    var observer = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === 'childList') {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            var node = mutation.addedNodes[j];
            if (node.id === 'appointo-modal' || (node.querySelector && node.querySelector('#appointo-modal'))) {
              checkModalState();
              var modalEl = document.getElementById('appointo-modal');
              if (modalEl) observer.observe(modalEl, { attributes: true, attributeFilter: ['style'] });
            }
          }
        }
        if (mutation.type === 'attributes' && mutation.target.id === 'appointo-modal') {
          checkModalState();
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', function(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('#appointo-btn, [id*="appointo-btn"]')) {
        requestAnimationFrame(function() { setTimeout(checkModalState, 100); });
      }
      if (target.closest('.appointo-close, .appointo-close-container')) {
        requestAnimationFrame(function() { setTimeout(checkModalState, 100); });
      }
    }, true);

    setInterval(checkModalState, 500);
  }

  /* ================================================================
     INIT — All functions run after DOM is ready
     ================================================================ */
  patchFaqToc();
  //initTrustpilotWidgetCleanup();
  //initPrivacyBannerChatGuard();
  //nitMobileChatLauncherGuard();
  initAppointoCheckoutFastPath();
  initAppointoModalChatGuard();

})();
